import type { FormEvent, KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';
import { PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';

import type { GovernedLexModel, LexDocumentAttachment, LexSelectionContext } from '../types/runtime';

type DocumentAction =
  | 'summary'
  | 'detailed_analysis'
  | 'key_issues'
  | 'dates_deadlines'
  | 'risks'
  | 'follow_up_questions';

const documentActions: Array<{ key: DocumentAction; label: string }> = [
  { key: 'summary', label: 'Summarize' },
  { key: 'detailed_analysis', label: 'Detailed legal analysis' },
  { key: 'key_issues', label: 'Key issues' },
  { key: 'dates_deadlines', label: 'Dates/deadlines' },
  { key: 'risks', label: 'Risks' },
  { key: 'follow_up_questions', label: 'Follow-up questions' },
];

interface Props {
  onSend: (text: string, selectionContext?: LexSelectionContext | null) => void;
  onOpenAttach?: () => void;
  onRemoveAttachment?: (attachmentId: string) => void;
  onDocumentAction?: (action: DocumentAction) => void;
  onModelChange?: (modelId: string | null) => void;
  isLoading: boolean;
  attachedDocuments?: LexDocumentAttachment[];
  availableModels?: GovernedLexModel[];
  selectedModelId?: string | null;
  modelPolicyLoading?: boolean;
  modelPolicyError?: string | null;
  promptSelection?: LexSelectionContext | null;
  onClearPromptSelection?: () => void;
  prefillText?: string | null;
}

export function LexInputBar({
  onSend,
  onOpenAttach,
  onRemoveAttachment,
  onDocumentAction,
  onModelChange,
  isLoading,
  attachedDocuments = [],
  availableModels = [],
  selectedModelId = null,
  modelPolicyLoading = false,
  modelPolicyError = null,
  promptSelection = null,
  onClearPromptSelection,
  prefillText = null,
}: Props) {
  const [text, setText] = useState('');
  const selectedModel = availableModels.find((model) => model.modelId === selectedModelId) ?? null;

  useEffect(() => {
    if (!prefillText) return;
    setText(prefillText);
  }, [prefillText]);

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (!text.trim() || isLoading) return;
    onSend(text, promptSelection);
    setText('');
    onClearPromptSelection?.();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <form
      onSubmit={submit}
      className="border-t border-slate-200 bg-white p-3"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Model</span>
          <select
            value={selectedModelId ?? ''}
            onChange={(event) => onModelChange?.(event.target.value || null)}
            disabled={isLoading || modelPolicyLoading || availableModels.length === 0}
            className="max-w-[320px] rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:border-slate-500 focus:outline-none disabled:opacity-50"
            title="Governed model selector"
          >
            {availableModels.length === 0 && (
              <option value="">{modelPolicyLoading ? 'Loading models...' : 'No governed models'}</option>
            )}
            {availableModels.map((model) => (
              <option key={model.modelId} value={model.modelId}>
                {model.displayName} · {model.providerKey}{model.isDefault ? ' · default' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="text-xs text-slate-500">
          {modelPolicyError
            ? 'Model policy unavailable'
            : selectedModel
              ? `LangGraph governed · ${selectedModel.providerKey}`
              : 'EdgeLex policy enforced'}
        </div>
      </div>
      {attachedDocuments.length > 0 && (
        <div className="mb-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            {attachedDocuments.map((attachment) => (
              <span
                key={attachment.id}
                className="inline-flex max-w-[320px] items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700"
                title={attachment.title}
              >
                <span className="truncate">
                  {attachment.source === 'dms' ? 'EdgeDocs' : 'Local'} · {attachment.title}
                </span>
                {onRemoveAttachment && (
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(attachment.id)}
                    className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                    aria-label={`Remove ${attachment.title}`}
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {documentActions.map((action) => (
              <button
                key={action.key}
                type="button"
                onClick={() => onDocumentAction?.(action.key)}
                disabled={isLoading}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {promptSelection && (
        <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-950">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold">Selected text attached</div>
              <div className="mt-1 line-clamp-2 leading-relaxed">
                {promptSelection.text}
              </div>
              <div className="mt-1 text-[11px] text-blue-700">
                {promptSelection.documentTitle || promptSelection.label || promptSelection.sourceSurface}
                {promptSelection.page ? ` · page ${promptSelection.page}` : ''}
              </div>
            </div>
            <button
              type="button"
              onClick={onClearPromptSelection}
              className="rounded-full p-0.5 text-blue-500 hover:bg-blue-100 hover:text-blue-800"
              aria-label="Remove selected text context"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      <div className="flex items-end gap-2">
        <button
          type="button"
          onClick={onOpenAttach}
          disabled={isLoading}
          className="rounded-lg border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          title="Attach document"
          aria-label="Attach document"
        >
          <PaperClipIcon className="h-5 w-5" />
        </button>
        <textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask Lex about a matter, document, related emails, or a next step..."
          className="flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
          disabled={isLoading}
        />
        <button
          type="submit"
          disabled={isLoading || !text.trim()}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          Send
        </button>
      </div>
    </form>
  );
}
